import { useCallback } from 'react';
import { FieldErrors, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getCanonical } from 'services/datachecks/datachecks';
import { logError } from 'services/logging/logging';
import {
  RelationshipDocumentCreationSuccessResponse,
  useRelationshipDocumentCreation,
} from 'services/repositories/relationships/relationships';
import { useHistoryStore } from 'stores/history/history';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { useAnalytics } from 'support/helpers/analytics/analytics';
import { GenericError, isDuplicatedDocumentError } from 'support/helpers/errors/errors';
import { countTrueValues, useIntervalSinceMount } from 'support/helpers/generic/generic';
import { routeToProductCatalogViewPage, routeToTransactionPage } from 'support/helpers/navigation/navigation';
import { enrichDocument } from 'support/helpers/transformer/transformer';
import { DocumentType, ProcessSpecificationDTO, RelationshipStatus, RelationshipV2 } from 'support/types';
import { removeInternalKeys } from 'pages/Transactions/utils';
import { OcrResult } from 'components/WebEDI/DocumentExtractor/types';
import { recordExtractionQuality } from 'components/WebEDI/DocumentExtractor/utils';

type PrepareDocumentResult = {
  enrichedCanonical: Record<string, unknown>;
  numberOfDirtyFields: number;
};

export const useDocumentSubmission = (
  targetRelationship: RelationshipV2 | undefined,
  nonVirtualDocumentType: DocumentType | undefined,
  ocrResult: OcrResult | undefined,
  inProductCatalogRoute: boolean,
) => {
  const navigate = useNavigate();
  const { previousPath } = useHistoryStore();
  const { track } = useAnalytics();
  const getIntervalSinceMount = useIntervalSinceMount();
  const { t } = useTranslation();
  const { mutate: createDocument, isLoading: isCreating, isSuccess } = useRelationshipDocumentCreation();

  const prepareDocumentForSubmit = useCallback(
    async (
      processSpecificationFields: ProcessSpecificationDTO['fields'],
      currentValues: FieldValues,
      messageType: DocumentType,
      currentDirtyFields: FieldErrors<Record<string, unknown>>,
    ): Promise<PrepareDocumentResult> => {
      const { canonical } = await getCanonical(processSpecificationFields, currentValues);
      const { data: enrichedCanonical } = enrichDocument({
        data: removeInternalKeys(canonical),
        messageType: messageType,
      });
      const numberOfDirtyFields = countTrueValues({ ...currentDirtyFields, info: false });
      return { enrichedCanonical, numberOfDirtyFields };
    },
    [],
  );

  const handleSubmissionSuccessNavigation = useCallback(
    (response: RelationshipDocumentCreationSuccessResponse) => {
      if (
        previousPath &&
        targetRelationship &&
        ![RelationshipStatus.LIVE, RelationshipStatus.ACTIVE].includes(RelationshipStatus[targetRelationship.status])
      ) {
        return navigate(previousPath);
      }

      navigate(
        inProductCatalogRoute
          ? routeToProductCatalogViewPage(response.transactionType, response.transactionId)
          : routeToTransactionPage(response.transactionType, response.transactionId),
        { state: { keepBreadcrumbs: true } },
      );
    },
    [targetRelationship, navigate, previousPath, inProductCatalogRoute],
  );

  const handleSubmissionError = useCallback(
    (e: Error) => {
      if (!nonVirtualDocumentType) {
        return;
      }

      const documentTypeTranslation = t(`common:messageTypes.plural.${nonVirtualDocumentType}`);
      if (isDuplicatedDocumentError(e)) {
        addNotification(
          {
            title: t('webedi:notifications.duplicatedDocument.title', { documentType: documentTypeTranslation }),
            subtitle: t('webedi:notifications.duplicatedDocument.subtitle', { documentType: documentTypeTranslation }),
          },
          NotificationType.error,
        );
        return;
      }
      logError(e);
      logError(new GenericError('[WebEDIV2] Failed to create document: ', { errorMessage: e.message }));
      addNotification(
        {
          title: t('webedi:notifications.createDocumentBackendFailed.title', { documentType: documentTypeTranslation }),
          subtitle: t('webedi:notifications.createDocumentBackendFailed.subtitle', {
            documentType: documentTypeTranslation,
          }),
        },
        NotificationType.error,
      );
    },
    [nonVirtualDocumentType, t],
  );

  const handleSubmissionSuccess = useCallback(
    (
      response: RelationshipDocumentCreationSuccessResponse,
      enrichedCanonical: Record<string, unknown>,
      numberOfDirtyFields: number,
    ) => {
      if (!nonVirtualDocumentType) {
        return;
      }

      track('new_document_send', {
        document_type: nonVirtualDocumentType,
        time_to_submit: getIntervalSinceMount(),
        number_of_dirty_fields: numberOfDirtyFields,
      });

      if (ocrResult) {
        track('new_document_send_ocr', {
          document_type: nonVirtualDocumentType,
          time_to_submit: getIntervalSinceMount(),
          number_of_dirty_fields: numberOfDirtyFields,
        });

        recordExtractionQuality(ocrResult.documentHash, {
          merged: ocrResult.data ?? {},
          sent: enrichedCanonical,
          context: { transaction_id: response.transactionId, transaction_type: response.transactionType },
        });
      }

      handleSubmissionSuccessNavigation(response);
    },
    [nonVirtualDocumentType, track, getIntervalSinceMount, ocrResult, handleSubmissionSuccessNavigation],
  );

  const submitDocument = useCallback(
    async (
      values: FieldValues,
      dirtyFields: FieldErrors<Record<string, unknown>>,
      setConfirmationModalOpen: (open: boolean) => void,
    ) => {
      if (!targetRelationship || !nonVirtualDocumentType) {
        logError('Target relationship or document type not available for submission.');
        return;
      }

      const { processSpecificationFields } = useWebEDIStore.getState();
      if (!processSpecificationFields) {
        logError('No process specification found');
        return;
      }

      setConfirmationModalOpen(false);

      let enrichedCanonical: Record<string, unknown>;
      let numberOfDirtyFields: number;

      try {
        const result = await prepareDocumentForSubmit(
          processSpecificationFields,
          values,
          nonVirtualDocumentType,
          dirtyFields,
        );
        enrichedCanonical = result.enrichedCanonical;
        numberOfDirtyFields = result.numberOfDirtyFields;
      } catch (error) {
        logError(error);
        addNotification(t('common:error.generic'), NotificationType.error);
        return;
      }

      createDocument(
        {
          relationshipId: targetRelationship.id,
          data: enrichedCanonical,
          application: ocrResult ? 'PORTAL_OCR' : 'PORTAL_WEB_EDI',
        },
        {
          onSuccess: (response) => handleSubmissionSuccess(response, enrichedCanonical, numberOfDirtyFields),
          onError: handleSubmissionError,
        },
      );
    },
    [
      t,
      targetRelationship,
      nonVirtualDocumentType,
      prepareDocumentForSubmit,
      createDocument,
      ocrResult,
      handleSubmissionSuccess,
      handleSubmissionError,
    ],
  );

  return {
    submitDocument,
    prepareDocumentForSubmit,
    handleSubmissionError,
    handleSubmissionSuccessNavigation,
    isCreating,
    isSuccess,
  };
};
